/**
 * Where an opening's bottom sits on the surface it is drawn in.
 *
 * ONE question with four askers, which is why it lives here rather than in any of
 * them: the hole cut in a room-wall face (`PolygonWalls` via `cutsDownToFloor` +
 * `openingHoleSpanY`), the hole cut in a structure-wall SOLID
 * (`StructureWallBoxes`), the door LEAF (`Door.jsx`), and the floor patch under it
 * (`OpeningThresholds`). Every one of them used to decide it alone, and #2286 shows
 * what that costs: the leaf was taught to follow a re-seated sill while the two
 * carvers still snapped to the wall's bottom, so a door rendered with its doorway
 * open beneath it and its head buried in the wall.
 *
 * THE RULE. A door's hole is snapped to the surface's own bottom edge rather than
 * to its scanned sill, because RoomPlan does not reliably put `center.y` at
 * `floorY + height / 2` and honouring a sill wrong by a few centimetres leaves a
 * threshold-height bar across the doorway. A sill a REAL STEP above that bottom is
 * the opposite case — no capture noise is that big, so a pipeline pass put it there
 * (a stair landing, a cluster step, a reconstructed half-level) and it is then the
 * most reliable thing about the opening.
 *
 * The boundary is therefore one real step, `MIN_REAL_STEP_M` — the same figure
 * Step 1 clusters room floors by, and the same one every re-seating pass requires
 * before it moves a sill. Not a number of its own: the pipeline's notion of "a real
 * level rather than noise" is exactly the question being asked here.
 *
 * Pure: an opening and a surface floor in, a number or a span out.
 */

const { MIN_REAL_STEP_M } = require('./floorYReconciliation');

/**
 * A sill this far above the surface it sits in was PUT there.
 *
 * Below it, a sill and the surface's bottom differ only by upstream Y drift
 * (#774): measured across the 37 processed corpus structures, all 392 doors no
 * pass had re-seated sit within 3.9e-7 m of their host wall's base — the float
 * round-trip through `centre ± height / 2` — so in practice nothing legitimate
 * lands between zero and a real step.
 */
const OPENING_ELEVATED_SILL_LIFT_M = MIN_REAL_STEP_M;

/**
 * ...except at a SMALL measured step (`reconstructSmallSteps`), under 7" and
 * so under the lift above, where the pipeline kept a door's sill on the higher
 * floor and stamped it `_sillAtStep`. A wall on the lower side bottoms 8-17 cm
 * below that sill, and snapping there cut the doorway into the lower floor
 * with a pit under the higher one's threshold. Such a sill is followed once it
 * clears the surface by more than float noise.
 */
const OPENING_SMALL_STEP_SILL_LIFT_M = 0.02;

/** How far above a surface this opening's sill must be to be followed. */
function elevatedSillLift(opening) {
  return opening?._sillAtStep
    ? OPENING_SMALL_STEP_SILL_LIFT_M
    : OPENING_ELEVATED_SILL_LIFT_M;
}

/** An opening's own sill, from its centre and height. Null when it has neither. */
function openingSillY(opening) {
  const y = opening?.center?.y;
  const h = opening?.dimensions?.y;
  if (typeof y !== 'number' || typeof h !== 'number') return null;
  return y - h / 2;
}

/**
 * Is this opening's sill lifted clear of the surface it is drawn in?
 *
 * @param {Object} opening - the canonical opening (a slim copy has no geometry)
 * @param {number} surfaceFloorY - the drawn surface's own lowest point: a room
 *   wall's `getWallFloorLevel`, a face's `faceMinY`, a solid's wall bottom
 */
function isElevatedSillOpening(opening, surfaceFloorY) {
  const sill = openingSillY(opening);
  if (sill === null || typeof surfaceFloorY !== 'number') return false;
  return sill - surfaceFloorY > elevatedSillLift(opening);
}

/**
 * The vertical span an opening occupies in a surface: where its hole is cut and
 * where its leaf stands, which are the same answer by construction.
 *
 * `snapsToFloor` is the caller's own reading of WHAT the opening is — a door or a
 * demoed span cuts to the floor, a window keeps its sill, a face reached through
 * the coplanar bridge never snaps because its bottom is a different storey's floor
 * (`cutsDownToFloor` holds that logic). This decides only whether the sill
 * overrides it.
 *
 * The HEIGHT is preserved, never the top: the leaf is drawn from this bottom with
 * the opening's own height, so a span that kept the captured top instead would be
 * taller than the leaf standing in it by however far the sill had drifted. The two
 * carvers disagreed on exactly that — the face preserved the height, the solid
 * stretched to the top — and agreed only because the drift is zero.
 *
 * @returns {{bottom: number, top: number, followsSill: boolean}}
 */
function openingVerticalSpan(opening, surfaceFloorY, { snapsToFloor } = {}) {
  const height = opening?.dimensions?.y || 0;
  const sill = openingSillY(opening);
  const followsSill =
    !snapsToFloor || isElevatedSillOpening(opening, surfaceFloorY);
  const bottom = followsSill && sill !== null ? sill : surfaceFloorY;
  return { bottom, top: bottom + height, followsSill };
}

module.exports = {
  OPENING_ELEVATED_SILL_LIFT_M,
  OPENING_SMALL_STEP_SILL_LIFT_M,
  elevatedSillLift,
  openingSillY,
  isElevatedSillOpening,
  openingVerticalSpan,
};
